import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Switch,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { 
  Save as SaveIcon, 
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  Repeat as RepeatIcon,
  CalendarToday as CalendarIcon,
  ContentCopy as CopyIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Person as PersonIcon
} from '@mui/icons-material';

interface ScheduleEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: ScheduleFormData) => Promise<void>;
  initialData?: ScheduleFormData;
  staffName?: string;
  date?: string;
  mode?: 'edit' | 'create';
  staffList?: Array<{ id: string; fullName: string }>;
  currentWeek?: number;
  getStaffScheduleForWeek?: (staff: any) => any;
  storeData?: any; // Store data containing opening/closing hours
}

interface ScheduleFormData {
  startTime: string;
  endTime: string;
  startBreak?: string;
  endBreak?: string;
  hasBreak: boolean;
  // New fields for combined functionality
  scheduleType?: 'single' | 'recurring';
  selectedStaffId?: string;
  recurringWeeks?: number;
  selectedDays?: string[];
  selectedWeek?: Date; // Week start date
  weeklySchedule?: WeeklyScheduleData; // Individual day schedules
  activeSchedules?: Array<{
    day: string;
    startTime: string;
    endTime: string;
    hasBreak: boolean;
    breakStart?: string;
    breakEnd?: string;
  }>;
  times?: Array<{
    startTime: string | null;
    endTime: string | null;
    startBreak?: string | null;
    endBreak?: string | null;
    isActive: boolean;
  }>;
}

interface WeeklyScheduleData {
  [key: string]: DaySchedule | null; // key is day name (monday, tuesday, etc)
}

interface DaySchedule {
  startTime: string;
  endTime: string;
  breakStart?: string;
  breakEnd?: string;
  hasBreak: boolean;
  isActive: boolean; // Whether staff works on this day
}

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday', short: 'Mon', full: 'Monday' },
  { key: 'tuesday', label: 'Tuesday', short: 'Tue', full: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday', short: 'Wed', full: 'Wednesday' },
  { key: 'thursday', label: 'Thursday', short: 'Thu', full: 'Thursday' },
  { key: 'friday', label: 'Friday', short: 'Fri', full: 'Friday' },
  { key: 'saturday', label: 'Saturday', short: 'Sat', full: 'Saturday' },
  { key: 'sunday', label: 'Sunday', short: 'Sun', full: 'Sunday' }
];

const ScheduleEditDialog: React.FC<ScheduleEditDialogProps> = ({
  open,
  onClose,
  onSave,
  initialData,
  staffName,
  date,
  mode = 'create',
  staffList = [],
  currentWeek = 0,
  getStaffScheduleForWeek,
  storeData
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  
  // Helper function to get store hours based on weekday/weekend
  const getStoreHours = (isWeekend: boolean = false) => {
    if (!storeData) {
      return { startTime: '09:00', endTime: '17:00' };
    }
    
    return {
      startTime: isWeekend ? storeData.weekendStartTime : storeData.weekdayStartTime,
      endTime: isWeekend ? storeData.weekendEndTime : storeData.weekdayEndTime
    };
  };
  
  const [formData, setFormData] = useState<ScheduleFormData>({
    startTime: initialData?.startTime || getStoreHours(false).startTime, // Default to weekday hours
    endTime: initialData?.endTime || getStoreHours(false).endTime, // Default to weekday hours
    startBreak: initialData?.startBreak || '12:00',
    endBreak: initialData?.endBreak || '13:00',
    hasBreak: initialData?.hasBreak || false,
    scheduleType: initialData?.scheduleType || 'single',
    selectedStaffId: initialData?.selectedStaffId || '',
    recurringWeeks: initialData?.recurringWeeks || 1,
    selectedDays: initialData?.selectedDays || [],
    selectedWeek: initialData?.selectedWeek || getStartOfWeek(new Date()),
    weeklySchedule: initialData?.weeklySchedule || getDefaultWeeklySchedule()
  });

  const [selectedDays, setSelectedDays] = useState<string[]>(
    initialData?.selectedDays || []
  );

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({
        startTime: initialData.startTime || getStoreHours(false).startTime, // Default to weekday hours
        endTime: initialData.endTime || getStoreHours(false).endTime, // Default to weekday hours
        startBreak: initialData.startBreak || '12:00',
        endBreak: initialData.endBreak || '13:00',
        hasBreak: initialData.hasBreak || false,
        scheduleType: initialData.scheduleType || 'single',
        selectedStaffId: initialData.selectedStaffId || '',
        recurringWeeks: initialData.recurringWeeks || 1,
        selectedDays: initialData.selectedDays || [],
        selectedWeek: initialData.selectedWeek || getStartOfWeek(new Date()),
        weeklySchedule: initialData.weeklySchedule || getDefaultWeeklySchedule()
      });
      setSelectedDays(initialData.selectedDays || []);
    }
  }, [initialData]);

  // Update form data when current week changes
  useEffect(() => {
    if (formData.selectedStaffId && getStaffScheduleForWeek) {
      // Get staff member from staffList
      const staffMember = staffList.find(s => s.id === formData.selectedStaffId);
      if (staffMember) {
        // Get schedules for the current week
        const updatedWeeklySchedule = getUpdatedWeeklyScheduleForCurrentWeek(staffMember);
        setFormData(prev => ({
          ...prev,
          weeklySchedule: updatedWeeklySchedule
        }));
      }
    }
  }, [currentWeek, formData.selectedStaffId, getStaffScheduleForWeek, staffList]);

  // Update weekly schedule when staff member changes
  useEffect(() => {
    if (formData.selectedStaffId && getStaffScheduleForWeek && formData.selectedWeek) {
      const updatedWeeklySchedule = getWeeklyScheduleForWeek(formData.selectedWeek, formData.selectedStaffId);
      setFormData(prev => ({
        ...prev,
        weeklySchedule: updatedWeeklySchedule
      }));
    }
  }, [formData.selectedStaffId, formData.selectedWeek, getStaffScheduleForWeek, staffList]);

  // Helper function to get weekly schedule for the current week
  const getUpdatedWeeklyScheduleForCurrentWeek = (staffMember: any) => {
    if (!getStaffScheduleForWeek) return getDefaultWeeklySchedule();
    
    const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const weeklySchedule: any = {};
    
    // Get schedules for this staff member for the current week
    const staffSchedules = getStaffScheduleForWeek(staffMember);
    
    dayKeys.forEach((dayKey, index) => {
      // Calculate the date for this day based on current week
      const startOfWeek = new Date();
      startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1 + (currentWeek * 7));
      const date = new Date(startOfWeek);
      date.setDate(date.getDate() + index);
      const dateKey = date.toISOString().split('T')[0];
      
      const schedule = staffSchedules[dateKey];
      
      if (schedule) {
        weeklySchedule[dayKey] = {
          startTime: schedule.startTime,
          endTime: schedule.endTime,
          breakStart: schedule.startBreak,
          breakEnd: schedule.endBreak,
          hasBreak: !!(schedule.startBreak && schedule.endBreak),
          isActive: true
        };
      } else {
        // Determine if this day is a weekend
        const isWeekend = dayKey === 'saturday' || dayKey === 'sunday';
        const storeHours = getStoreHours(isWeekend);
        
        weeklySchedule[dayKey] = {
          startTime: storeHours.startTime,
          endTime: storeHours.endTime,
          breakStart: '12:00',
          breakEnd: '13:00',
          hasBreak: false,
          isActive: false
        };
      }
    });
    
    return weeklySchedule;
  };

  // Helper function to get start of week (Monday)
  function getStartOfWeek(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    return new Date(d.setDate(diff));
  }

  // Helper function to get default weekly schedule
  function getDefaultWeeklySchedule(): WeeklyScheduleData {
    const defaultSchedule: WeeklyScheduleData = {};
    DAYS_OF_WEEK.forEach(day => {
      // Determine if this is a weekend day (Saturday = 5, Sunday = 6 in our 0-indexed Monday-Sunday array)
      const isWeekend = day.key === 'saturday' || day.key === 'sunday';
      
      // Get store hours based on weekday/weekend
      const storeHours = getStoreHours(isWeekend);
      
      defaultSchedule[day.key] = {
        startTime: storeHours.startTime,
        endTime: storeHours.endTime,
        breakStart: '12:00',
        breakEnd: '13:00',
        hasBreak: false,
        isActive: false
      };
    });
    
    // If we have a specific date (for single day scheduling), activate that day
    if (date && mode === 'create') {
      const scheduleDate = new Date(date);
      const dayIndex = scheduleDate.getDay();
      // Convert Sunday (0) to Saturday (6) for our 0-indexed Monday-Sunday array
      const adjustedIndex = dayIndex === 0 ? 6 : dayIndex - 1;
      const dayKey = DAYS_OF_WEEK[adjustedIndex].key;
      
      if (defaultSchedule[dayKey]) {
        defaultSchedule[dayKey]!.isActive = true;
      }
    }
    
    return defaultSchedule;
  }

  // Helper function to format time to HH:MM format
  const formatTimeDisplay = (time: string): string => {
    if (!time) return '';
    // If time already in HH:MM format, return as is
    if (time.length === 5 && time.includes(':')) {
      return time;
    }
    // If time includes seconds (HH:MM:SS), remove seconds
    if (time.length === 8 && time.split(':').length === 3) {
      return time.substring(0, 5);
    }
    return time;
  };

  // Helper function to format date for display
  function formatDateRange(startDate: Date): string {
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 6);
    
    return `${startDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    })} - ${endDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    })}`;
  }

  // Helper function to create times array for GraphQL submission
  function createTimesArray(): Array<{
    startTime: string | null;
    endTime: string | null;
    startBreak?: string | null;
    endBreak?: string | null;
    isActive: boolean;
  }> {
    const times = [];
    
    // Create array for all 7 days (Monday = 0, Sunday = 6)
    for (let i = 0; i < 7; i++) {
      const dayKey = DAYS_OF_WEEK[i].key;
      const daySchedule = formData.weeklySchedule![dayKey];
      
      let isActive = false;
      // Get store hours for this day
      const isWeekend = dayKey === 'saturday' || dayKey === 'sunday';
      const storeHours = getStoreHours(isWeekend);
      let startTime = storeHours.startTime;
      let endTime = storeHours.endTime;
      let startBreak: string | undefined = undefined;
      let endBreak: string | undefined = undefined;
      
      if (isRecurringMode) {
        isActive = selectedDays.includes(dayKey);
        if (isActive) {
          // For recurring mode, use the main form times
          startTime = formData.startTime || storeHours.startTime;
          endTime = formData.endTime || storeHours.endTime;
          if (formData.hasBreak) {
            startBreak = formData.startBreak || '12:00';
            endBreak = formData.endBreak || '13:00';
          }
        }
      } else {
        // For single week mode, use individual day schedules
        isActive = daySchedule?.isActive || false;
        if (isActive && daySchedule) {
          startTime = daySchedule.startTime || storeHours.startTime;
          endTime = daySchedule.endTime || storeHours.endTime;
          if (daySchedule.hasBreak) {
            startBreak = daySchedule.breakStart || '12:00';
            endBreak = daySchedule.breakEnd || '13:00';
          }
        }
      }
      
      // For GraphQL, we need to send the actual time values even for inactive days
      // but only include break times if there's actually a break
      const timeEntry: any = {
        startTime: isActive ? startTime : null,
        endTime: isActive ? endTime : null,
        isActive
      };
      
      // Only add break times if they exist and the day is active
      if (isActive && startBreak && endBreak) {
        timeEntry.startBreak = startBreak;
        timeEntry.endBreak = endBreak;
      } else {
        timeEntry.startBreak = null;
        timeEntry.endBreak = null;
      }
      
      times.push(timeEntry);
    }
    
    console.log('Created times array:', {
      times,
      isRecurringMode,
      selectedDays,
      weeklySchedule: formData.weeklySchedule,
      activeDaysCount: times.filter(t => t.isActive).length
    });
    return times;
  }

  const isCreateMode = mode === 'create';
  const isRecurringMode = formData.scheduleType === 'recurring';

  // Helper function to get weekly schedule for a specific week
  const getWeeklyScheduleForWeek = (selectedWeek: Date, staffId: string): WeeklyScheduleData => {
    if (!getStaffScheduleForWeek || !staffId) {
      return getDefaultWeeklySchedule();
    }

    const staffMember = staffList.find(s => s.id === staffId);
    if (!staffMember) {
      return getDefaultWeeklySchedule();
    }

    const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const weeklySchedule: WeeklyScheduleData = {};
    
    dayKeys.forEach((dayKey, index) => {
      // Calculate the date for this day based on selected week
      const weekStart = new Date(selectedWeek);
      const dayDate = new Date(weekStart);
      dayDate.setDate(dayDate.getDate() + index);
      const dateKey = dayDate.toISOString().split('T')[0];
      
      // Get staff schedules for the selected week
      const staffSchedules = getStaffScheduleForWeek(staffMember);
      const schedule = staffSchedules[dateKey];
      
      if (schedule) {
        weeklySchedule[dayKey] = {
          startTime: schedule.startTime,
          endTime: schedule.endTime,
          breakStart: schedule.startBreak,
          breakEnd: schedule.endBreak,
          hasBreak: !!(schedule.startBreak && schedule.endBreak),
          isActive: true
        };
      } else {
        // Determine if this day is a weekend
        const isWeekend = dayKey === 'saturday' || dayKey === 'sunday';
        const storeHours = getStoreHours(isWeekend);
        
        weeklySchedule[dayKey] = {
          startTime: storeHours.startTime,
          endTime: storeHours.endTime,
          breakStart: '12:00',
          breakEnd: '13:00',
          hasBreak: false,
          isActive: false
        };
      }
    });
    
    return weeklySchedule;
  };

  // Week navigation functions
  const handlePreviousWeek = () => {
    const newWeek = new Date(formData.selectedWeek!);
    newWeek.setDate(newWeek.getDate() - 7);
    
    // Update the week and refresh the weekly schedule data
    const updatedWeeklySchedule = getWeeklyScheduleForWeek(newWeek, formData.selectedStaffId || '');
    
    setFormData(prev => ({ 
      ...prev, 
      selectedWeek: newWeek,
      weeklySchedule: updatedWeeklySchedule
    }));
  };

  const handleNextWeek = () => {
    const newWeek = new Date(formData.selectedWeek!);
    newWeek.setDate(newWeek.getDate() + 7);
    
    // Update the week and refresh the weekly schedule data
    const updatedWeeklySchedule = getWeeklyScheduleForWeek(newWeek, formData.selectedStaffId || '');
    
    setFormData(prev => ({ 
      ...prev, 
      selectedWeek: newWeek,
      weeklySchedule: updatedWeeklySchedule
    }));
  };

  // Day schedule management
  const handleDayToggle = (dayKey: string, isChecked?: boolean) => {
    if (isRecurringMode) {
      // For recurring mode, toggle day selection and update weekly schedule
      setSelectedDays(prev => {
        const newDays = prev.includes(dayKey)
          ? prev.filter(d => d !== dayKey)
          : [...prev, dayKey];
        
        // Get appropriate store hours based on day type
        const isWeekend = dayKey === 'saturday' || dayKey === 'sunday';
        const dayStoreHours = getStoreHours(isWeekend);
        
        // Update form data with selected days
        setFormData(prevForm => ({
          ...prevForm,
          selectedDays: newDays,
          weeklySchedule: {
            ...prevForm.weeklySchedule!,
            [dayKey]: newDays.includes(dayKey) ? {
              startTime: prevForm.startTime || dayStoreHours.startTime,
              endTime: prevForm.endTime || dayStoreHours.endTime,
              breakStart: prevForm.hasBreak ? prevForm.startBreak : undefined,
              breakEnd: prevForm.hasBreak ? prevForm.endBreak : undefined,
              hasBreak: prevForm.hasBreak,
              isActive: true
            } : {
              ...prevForm.weeklySchedule![dayKey]!,
              isActive: false
            }
          }
        }));
        
        return newDays;
      });
    } else {
      // For single week mode, toggle working day
      setFormData(prev => ({
        ...prev,
        weeklySchedule: {
          ...prev.weeklySchedule!,
          [dayKey]: {
            ...prev.weeklySchedule![dayKey]!,
            isActive: isChecked !== undefined ? isChecked : !prev.weeklySchedule![dayKey]!.isActive
          }
        }
      }));
    }
  };

  // Update day schedule
  const handleDayScheduleChange = (dayKey: string, field: keyof DaySchedule, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      weeklySchedule: {
        ...prev.weeklySchedule!,
        [dayKey]: {
          ...prev.weeklySchedule![dayKey]!,
          [field]: value
        }
      }
    }));
  };

  // Copy day schedule to all other working days
  const handleCopyToAllDays = (sourceDayKey: string) => {
    const sourceSchedule = formData.weeklySchedule![sourceDayKey];
    if (!sourceSchedule) return;

    setFormData(prev => {
      const newWeeklySchedule = { ...prev.weeklySchedule! };
      Object.keys(newWeeklySchedule).forEach(dayKey => {
        if (newWeeklySchedule[dayKey] && dayKey !== sourceDayKey) {
          // Copy all schedule properties including break settings
          newWeeklySchedule[dayKey] = { 
            ...sourceSchedule,
            // Preserve the isActive state of the target day
            isActive: newWeeklySchedule[dayKey]!.isActive
          };
        }
      });
      return { ...prev, weeklySchedule: newWeeklySchedule };
    });
  };

  // Schedule type management
  const handleScheduleTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newType: 'single' | 'recurring'
  ) => {
    if (newType !== null) {
      setFormData(prev => ({
        ...prev,
        scheduleType: newType
      }));
    }
  };

  const handleInputChange = (field: keyof ScheduleFormData, value: string | number | boolean) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // If staff selection changes, update weekly schedule for the selected week
      if (field === 'selectedStaffId' && value && formData.selectedWeek) {
        const updatedWeeklySchedule = getWeeklyScheduleForWeek(formData.selectedWeek, value as string);
        newData.weeklySchedule = updatedWeeklySchedule;
      }
      
      // If in recurring mode and changing schedule times, update all selected days
      if (isRecurringMode && (field === 'startTime' || field === 'endTime' || field === 'hasBreak' || field === 'startBreak' || field === 'endBreak')) {
        const updatedWeeklySchedule = { ...prev.weeklySchedule! };
        selectedDays.forEach(dayKey => {
          if (updatedWeeklySchedule[dayKey]) {
            // Get appropriate store hours based on day type as fallback
            const isWeekend = dayKey === 'saturday' || dayKey === 'sunday';
            const dayStoreHours = getStoreHours(isWeekend);
            
            updatedWeeklySchedule[dayKey] = {
              ...updatedWeeklySchedule[dayKey]!,
              startTime: field === 'startTime' ? (value as string) : (newData.startTime || dayStoreHours.startTime),
              endTime: field === 'endTime' ? (value as string) : (newData.endTime || dayStoreHours.endTime),
              hasBreak: field === 'hasBreak' ? value as boolean : newData.hasBreak,
              breakStart: (field === 'startBreak' ? value as string : newData.startBreak) || undefined,
              breakEnd: (field === 'endBreak' ? value as string : newData.endBreak) || undefined
            };
          }
        });
        newData.weeklySchedule = updatedWeeklySchedule;
      }
      
      return newData;
    });
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Staff selection is required for all create modes
    if (isCreateMode && !formData.selectedStaffId) {
      newErrors.selectedStaffId = 'Please select a staff member';
    }

    if (isRecurringMode) {
      // Validation for recurring schedules
      if (selectedDays.length === 0) {
        newErrors.selectedDays = 'Please select at least one day';
      }

      // Validate main schedule times for recurring
      if (!formData.startTime) {
        newErrors.startTime = 'Start time is required';
      }
      if (!formData.endTime) {
        newErrors.endTime = 'End time is required';
      }
      if (formData.startTime && formData.endTime && formData.startTime >= formData.endTime) {
        newErrors.endTime = 'End time must be after start time';
      }

      // Validate break times if break is enabled for recurring
      if (formData.hasBreak) {
        if (!formData.startBreak) {
          newErrors.startBreak = 'Break start time is required';
        }
        if (!formData.endBreak) {
          newErrors.endBreak = 'Break end time is required';
        }
        if (formData.startBreak && formData.endBreak && formData.startBreak >= formData.endBreak) {
          newErrors.endBreak = 'Break end time must be after break start time';
        }
      }

      // Validate recurring weeks
      if (!formData.recurringWeeks || formData.recurringWeeks < 1) {
        newErrors.recurringWeeks = 'Number of weeks must be at least 1';
      }
      if (formData.recurringWeeks && formData.recurringWeeks > 52) {
        newErrors.recurringWeeks = 'Number of weeks cannot exceed 52';
      }
    } else {
      // Validation for single week schedules
      const workingDays = Object.entries(formData.weeklySchedule || {}).filter(([_, schedule]) => schedule?.isActive);
      
      if (workingDays.length === 0) {
        newErrors.weeklySchedule = 'Please select at least one working day';
      }

      // Validate each working day
      workingDays.forEach(([dayKey, schedule]) => {
        if (!schedule) return;

        if (!schedule.startTime) {
          newErrors[`${dayKey}_startTime`] = `${DAYS_OF_WEEK.find(d => d.key === dayKey)?.label} start time is required`;
        }
        if (!schedule.endTime) {
          newErrors[`${dayKey}_endTime`] = `${DAYS_OF_WEEK.find(d => d.key === dayKey)?.label} end time is required`;
        }
        if (schedule.startTime && schedule.endTime && schedule.startTime >= schedule.endTime) {
          newErrors[`${dayKey}_endTime`] = `${DAYS_OF_WEEK.find(d => d.key === dayKey)?.label} end time must be after start time`;
        }

        if (schedule.hasBreak) {
          if (!schedule.breakStart) {
            newErrors[`${dayKey}_breakStart`] = `${DAYS_OF_WEEK.find(d => d.key === dayKey)?.label} break start time is required`;
          }
          if (!schedule.breakEnd) {
            newErrors[`${dayKey}_breakEnd`] = `${DAYS_OF_WEEK.find(d => d.key === dayKey)?.label} break end time is required`;
          }
          if (schedule.breakStart && schedule.breakEnd && schedule.breakStart >= schedule.breakEnd) {
            newErrors[`${dayKey}_breakEnd`] = `${DAYS_OF_WEEK.find(d => d.key === dayKey)?.label} break end time must be after break start time`;
          }
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      let dataToSave: ScheduleFormData;
      
      // Create the times array for GraphQL submission
      const timesArray = createTimesArray();

      if (isRecurringMode) {
        // For recurring schedules, use the main form data structure
        dataToSave = {
          ...formData,
          selectedStaffId: formData.selectedStaffId,
          startTime: formData.startTime,
          endTime: formData.endTime,
          hasBreak: formData.hasBreak,
          startBreak: formData.hasBreak ? formData.startBreak : undefined,
          endBreak: formData.hasBreak ? formData.endBreak : undefined,
          selectedDays: selectedDays,
          scheduleType: 'recurring',
          recurringWeeks: formData.recurringWeeks,
          selectedWeek: (() => {
            const startOfWeek = new Date();
            startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1 + (currentWeek * 7));
            return startOfWeek;
          })(),
          times: timesArray
        };
      } else {
        // For single week schedules, we need to structure the data differently
        const activeSchedules = Object.entries(formData.weeklySchedule || {})
          .filter(([_, schedule]) => schedule?.isActive)
          .map(([dayKey, schedule]) => ({
            day: dayKey,
            startTime: schedule!.startTime,
            endTime: schedule!.endTime,
            hasBreak: schedule!.hasBreak,
            breakStart: schedule!.hasBreak ? schedule!.breakStart : undefined,
            breakEnd: schedule!.hasBreak ? schedule!.breakEnd : undefined
          }));

        dataToSave = {
          ...formData,
          selectedStaffId: formData.selectedStaffId,
          scheduleType: 'single',
          selectedWeek: (() => {
            const startOfWeek = new Date();
            startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1 + (currentWeek * 7));
            return startOfWeek;
          })(),
          weeklySchedule: formData.weeklySchedule,
          // Also include the structured data for easier backend processing
          activeSchedules,
          times: timesArray
        };
      }

      console.log('Saving schedule data:', {
        staffId: dataToSave.selectedStaffId,
        scheduleType: dataToSave.scheduleType,
        isRecurring: isRecurringMode,
        selectedDays: selectedDays,
        weeklySchedule: dataToSave.weeklySchedule,
        times: dataToSave.times,
        timesArrayLength: timesArray.length,
        activeDays: timesArray.filter(t => t.isActive).length,
        timesDetail: timesArray.map((t, i) => ({
          index: i,
          day: DAYS_OF_WEEK[i].label,
          ...t
        })),
        formDataState: {
          startTime: formData.startTime,
          endTime: formData.endTime,
          hasBreak: formData.hasBreak,
          startBreak: formData.startBreak,
          endBreak: formData.endBreak
        },
        fullData: dataToSave
      });
      
      await onSave(dataToSave);
      onClose();
    } catch (error) {
      console.error('Error saving schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          boxShadow: theme.shadows[8]
        }
      }}
    >
      <DialogTitle sx={{
        position: 'relative',
        pb: 2,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.default',
        px: { xs: 2, sm: 3 },
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1.5,
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ScheduleIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h6" component="div" fontWeight={600}>
              Manage Schedule
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {(() => {
                const startOfWeek = new Date();
                startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1 + (currentWeek * 7));
                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(endOfWeek.getDate() + 6);
                const weekText = `${startOfWeek.toLocaleDateString()} - ${endOfWeek.toLocaleDateString()}`;
                const currentWeekText = currentWeek === 0 ? ' (Current Week)' : '';
                return `Week: ${weekText}${currentWeekText}`;
              })()}
            </Typography>
            {staffName && date && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {staffName} • {new Date(date).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: 'text.secondary',
            '&:hover': { bgcolor: 'action.hover' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: { xs: 1.5, sm: 3 }, bgcolor: 'background.default' }}>
        {/* Error Alert */}
        {Object.keys(errors).length > 0 && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 1.5 }}>
            Please fix the following errors before saving:
            <ul style={{ margin: '8px 0 0 16px' }}>
              {Object.values(errors).map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Staff Assignment Section */}
        {isCreateMode && (
          <Card sx={{ 
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            mb: 3
          }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1.5,
                    backgroundColor: theme.palette.info.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <PersonIcon fontSize="small" />
                </Box>
                <Typography variant="h6" fontWeight={600}>
                  Staff Assignment
                </Typography>
              </Box>
              
              <FormControl fullWidth error={!!errors.selectedStaffId}>
                <InputLabel>Select Staff Member</InputLabel>
                <Select
                  value={formData.selectedStaffId}
                  onChange={(e) => handleInputChange('selectedStaffId', e.target.value)}
                  label="Select Staff Member"
                >
                  {staffList.map((staff) => (
                    <MenuItem key={staff.id} value={staff.id}>
                      {staff.fullName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.selectedStaffId && (
                  <Typography variant="caption" color="error">
                    {errors.selectedStaffId}
                  </Typography>
                )}
              </FormControl>
            </CardContent>
          </Card>
        )}

        {/* Schedule Type Section (for create mode) */}
        {isCreateMode && (
          <Card sx={{ 
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            mb: 3
          }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1.5,
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <RepeatIcon fontSize="small" />
                </Box>
                <Typography variant="h6" fontWeight={600}>
                  Schedule Type
                </Typography>
              </Box>

              <ToggleButtonGroup
                value={formData.scheduleType}
                exclusive
                onChange={handleScheduleTypeChange}
                fullWidth
                sx={{ mb: 2 }}
              >
                <ToggleButton value="single" sx={{ py: 1, px: { xs: 1.5, sm: 3 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  <CalendarIcon sx={{ mr: 0.5, fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />
                  Single Week
                </ToggleButton>
                <ToggleButton value="recurring" sx={{ py: 1, px: { xs: 1.5, sm: 3 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  <RepeatIcon sx={{ mr: 0.5, fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />
                  Recurring
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Week Selector for Single Week Mode */}
              {!isRecurringMode && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Select Week
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, sm: 2 },
                    p: { xs: 1.5, sm: 2 },
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    bgcolor: 'background.default'
                  }}>
                    <IconButton
                      onClick={handlePreviousWeek}
                      size="small"
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'primary.dark' },
                        flexShrink: 0,
                      }}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <Box sx={{ flexGrow: 1, textAlign: 'center', minWidth: 0 }}>
                      <Typography variant="body1" fontWeight={600} sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                        {formatDateRange(formData.selectedWeek!)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        Week of {formData.selectedWeek!.toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={handleNextWeek}
                      size="small"
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'primary.dark' },
                        flexShrink: 0,
                      }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Box>
                </Box>
              )}

              {/* Recurring Schedule Options */}
              {isRecurringMode && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Recurring Schedule Options
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Number of Weeks"
                        type="number"
                        value={formData.recurringWeeks}
                        onChange={(e) => handleInputChange('recurringWeeks', parseInt(e.target.value) || 1)}
                        inputProps={{ min: 1, max: 52 }}
                        error={!!errors.recurringWeeks}
                        helperText={errors.recurringWeeks || 'How many weeks to repeat this schedule'}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Starting Week"
                        type="date"
                        value={formData.selectedWeek?.toISOString().split('T')[0] || ''}
                        onChange={(e) => {
                          const selectedDate = new Date(e.target.value);
                          const startOfWeek = getStartOfWeek(selectedDate);
                          setFormData(prev => ({ ...prev, selectedWeek: startOfWeek }));
                        }}
                        InputLabelProps={{ shrink: true }}
                        helperText="Select any day in the week you want to start"
                      />
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Select Days:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {DAYS_OF_WEEK.map((day) => (
                        <Chip
                          key={day.key}
                          label={day.short}
                          onClick={() => handleDayToggle(day.key)}
                          color={selectedDays.includes(day.key) ? 'primary' : 'default'}
                          variant={selectedDays.includes(day.key) ? 'filled' : 'outlined'}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Box>
                    {errors.selectedDays && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                        {errors.selectedDays}
                      </Typography>
                    )}
                  </Box>

                  {/* Working Hours for Recurring Schedule */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Working Hours:
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Start Time"
                          type="time"
                          value={formData.startTime || getStoreHours(false).startTime}
                          onChange={(e) => handleInputChange('startTime', e.target.value)}
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }} // 5 minute intervals, no seconds
                          error={!!errors.startTime}
                          helperText={errors.startTime}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="End Time"
                          type="time"
                          value={formData.endTime || getStoreHours(false).endTime}
                          onChange={(e) => handleInputChange('endTime', e.target.value)}
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }} // 5 minute intervals, no seconds
                          error={!!errors.endTime}
                          helperText={errors.endTime}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Break Settings for Recurring Schedule */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Break Settings:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Switch
                        checked={formData.hasBreak || false}
                        onChange={(e) => handleInputChange('hasBreak', e.target.checked)}
                        color="primary"
                        size="small"
                      />
                      <Typography variant="body2">
                        Include breaks in schedule
                      </Typography>
                    </Box>
                    
                    {formData.hasBreak && (
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Break Start Time"
                            type="time"
                            value={formData.startBreak || '12:00'}
                            onChange={(e) => handleInputChange('startBreak', e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 300 }} // 5 minute intervals, no seconds
                            error={!!errors.startBreak}
                            helperText={errors.startBreak}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Break End Time"
                            type="time"
                            value={formData.endBreak || '13:00'}
                            onChange={(e) => handleInputChange('endBreak', e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 300 }} // 5 minute intervals, no seconds
                            error={!!errors.endBreak}
                            helperText={errors.endBreak}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Weekly Schedule Section */}
        <Card sx={{ 
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          mb: 3
        }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CalendarIcon fontSize="small" />
              </Box>
              <Typography variant="h6" fontWeight={600}>
                Weekly Schedule
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 1.5, sm: 3 }}>
              {DAYS_OF_WEEK.map((day) => {
                const daySchedule = formData.weeklySchedule![day.key];
                const isActive = isRecurringMode ? selectedDays.includes(day.key) : daySchedule?.isActive;

                return (
                  <Grid item xs={12} md={6} key={day.key}>
                    <Card
                      sx={{
                        border: 1,
                        borderColor: isActive ? 'primary.main' : 'divider',
                        bgcolor: isActive ? 'primary.50' : 'background.default',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {day.full}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {isActive && (
                              <IconButton
                                onClick={() => handleCopyToAllDays(day.key)}
                                size="small"
                                title={`Copy ${day.label} schedule to all other days`}
                                sx={{ 
                                  bgcolor: 'primary.main',
                                  color: 'white',
                                  '&:hover': { bgcolor: 'primary.dark' }
                                }}
                              >
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            )}
                            {!isRecurringMode && (
                              <Switch
                                checked={isActive || false}
                                onChange={(e) => handleDayToggle(day.key, e.target.checked)}
                                color="primary"
                                size="small"
                              />
                            )}
                          </Box>
                        </Box>

                        {isActive && daySchedule && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <TextField
                                label="Start Time"
                                type="time"
                                value={daySchedule.startTime || getStoreHours(day.key === 'saturday' || day.key === 'sunday').startTime}
                                onChange={(e) => handleDayScheduleChange(day.key, 'startTime', e.target.value)}
                                size="small"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 300 }} // 5 minute intervals, no seconds
                              />
                              <TextField
                                label="End Time"
                                type="time"
                                value={daySchedule.endTime || getStoreHours(day.key === 'saturday' || day.key === 'sunday').endTime}
                                onChange={(e) => handleDayScheduleChange(day.key, 'endTime', e.target.value)}
                                size="small"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 300 }} // 5 minute intervals, no seconds
                              />
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Break:
                              </Typography>
                              <Switch
                                checked={daySchedule.hasBreak || false}
                                onChange={(e) => handleDayScheduleChange(day.key, 'hasBreak', e.target.checked)}
                                color="primary"
                                size="small"
                              />
                            </Box>

                            {daySchedule.hasBreak && (
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                  label="Break Start"
                                  type="time"
                                  value={daySchedule.breakStart || '12:00'}
                                  onChange={(e) => handleDayScheduleChange(day.key, 'breakStart', e.target.value)}
                                  size="small"
                                  fullWidth
                                  InputLabelProps={{ shrink: true }}
                                  inputProps={{ step: 300 }} // 5 minute intervals, no seconds
                                />
                                <TextField
                                  label="Break End"
                                  type="time"
                                  value={daySchedule.breakEnd || '13:00'}
                                  onChange={(e) => handleDayScheduleChange(day.key, 'breakEnd', e.target.value)}
                                  size="small"
                                  fullWidth
                                  InputLabelProps={{ shrink: true }}
                                  inputProps={{ step: 300 }} // 5 minute intervals, no seconds
                                />
                              </Box>
                            )}

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Hours:
                              </Typography>
                              <Chip
                                label={`${formatTimeDisplay(daySchedule.startTime || getStoreHours(day.key === 'saturday' || day.key === 'sunday').startTime)} - ${formatTimeDisplay(daySchedule.endTime || getStoreHours(day.key === 'saturday' || day.key === 'sunday').endTime)}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              {daySchedule.hasBreak && daySchedule.breakStart && daySchedule.breakEnd && (
                                <>
                                  <Typography variant="body2" color="text.secondary">
                                    Break:
                                  </Typography>
                                  <Chip
                                    label={`${formatTimeDisplay(daySchedule.breakStart)} - ${formatTimeDisplay(daySchedule.breakEnd)}`}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                  />
                                </>
                              )}
                            </Box>
                          </Box>
                        )}

                        {!isActive && (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            py: 2,
                            color: 'text.secondary'
                          }}>
                            <Typography variant="body2">
                              Day Off
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* Schedule Summary */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Schedule Summary
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {DAYS_OF_WEEK.map((day) => {
                  const daySchedule = formData.weeklySchedule![day.key];
                  const isActive = isRecurringMode ? selectedDays.includes(day.key) : daySchedule?.isActive;
                  
                  return (
                    <Chip
                      key={day.key}
                      label={isActive && daySchedule ? `${day.short}: ${formatTimeDisplay(daySchedule.startTime)}-${formatTimeDisplay(daySchedule.endTime)}` : `${day.short}: Off`}
                      size="small"
                      color={isActive ? 'primary' : 'default'}
                      variant={isActive ? 'filled' : 'outlined'}
                    />
                  );
                })}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </DialogContent>
      
      <DialogActions sx={{
        p: { xs: 2, sm: 3 },
        gap: 1,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'background.default',
        flexDirection: { xs: 'column-reverse', sm: 'row' },
        '& > button': { width: { xs: '100%', sm: 'auto' } }
      }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          sx={{ 
            borderRadius: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            px: 3
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          sx={{ 
            borderRadius: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            boxShadow: theme.shadows[2]
          }}
        >
          {loading ? 'Saving...' : 'Save Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleEditDialog;
